// Shared brochure download helpers.
// - Mobile (downloadAndPersistBrochureFiles): saves to device storage via
//   expo-file-system/fflate. A single file is saved as-is; multiple files
//   are zipped into one archive first:
//     - single file:  {projectName}-brochure.{format}
//     - multiple:     {projectName}-brochure.zip
// - Web (downloadBrochureFiles): triggers a browser download via JSZip.
import { downloadAndSaveFile } from '@/core/utils/fileDownload';
import { logger } from '@/core/logger';
import { Buffer } from 'node:buffer';
import { File, Paths } from 'expo-file-system';
import { Zip, ZipPassThrough } from 'fflate';
import JSZip from 'jszip';
import { Linking, Platform } from 'react-native';

export interface DownloadResult {
    success: boolean;
    fileUri?: string;
    fileName?: string;
    message?: string;
    error?: string;
    /**
     * True when the file was saved to app-private storage rather than the
     * shared Downloads folder — the caller must offer the OS share sheet for
     * the user to actually save/move it. Set on Android for multi-file zips,
     * since Android's SAF write (the only way to reach Downloads) requires
     * one full base64 string in memory, which OOMs for larger zips.
     */
    requiresShareToSave?: boolean;
}

const mapExtToMime = (ext: string): string => {
    switch (ext) {
        case 'pdf':
            return 'application/pdf';
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'zip':
            return 'application/zip';
        default:
            return 'application/octet-stream';
    }
};

function extractExtension(url: string): string {
    const match = /\.([a-zA-Z0-9]+)$/.exec(url.split('?')[0] || '');
    return (match?.[1] || 'pdf').toLowerCase();
}

function sanitizeProjectName(projectName: string): string {
    const cleaned = projectName.trim().replace(/[^a-zA-Z0-9-_ ]+/g, '').trim().replace(/\s+/g, '-');
    return cleaned || 'Property';
}

async function fetchFileBytes(url: string): Promise<{ bytes: Uint8Array; ext: string }> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Document not available');
    }
    const arrayBuffer = await response.arrayBuffer();
    return { bytes: new Uint8Array(arrayBuffer), ext: extractExtension(url) };
}

// Keeps peak memory bounded on Android regardless of source file size —
// large brochure images (tens of MB) blew past the JS heap when fetched
// whole via fetch()/arrayBuffer(). iOS isn't touched — it already works.
const ANDROID_ZIP_CHUNK_BYTES = 1024 * 1024;

/**
 * Android-only: downloads a file straight to disk (native, no JS-heap
 * allocation for the network response) then streams it into the zip in
 * fixed-size chunks, so only one chunk is ever held in memory at a time.
 */
async function streamAndroidFileIntoZipEntry(
    url: string,
    safeName: string,
    index: number,
    total: number,
    zip: Zip,
): Promise<void> {
    const ext = extractExtension(url);
    const entryName = `${safeName}-brochure-${index + 1}.${ext}`;
    const tempFileName = `brochure-dl-${index + 1}-${Date.now()}.${ext}`;

    logger.info('brochureDownload', 'Multi-file path (Android): downloading file to disk', {
        index: index + 1,
        of: total,
        entryName,
    });
    const tempFile = await File.downloadFileAsync(url, new File(Paths.cache, tempFileName), { idempotent: true });

    const entry = new ZipPassThrough(entryName);
    zip.add(entry);

    const handle = tempFile.open();
    try {
        const totalSize = tempFile.size;
        logger.info('brochureDownload', 'Multi-file path (Android): downloaded, streaming into zip in chunks', {
            index: index + 1,
            of: total,
            entryName,
            byteLength: totalSize,
        });

        if (totalSize === 0) {
            entry.push(new Uint8Array(0), true);
        } else {
            let readTotal = 0;
            while (readTotal < totalSize) {
                const chunkSize = Math.min(ANDROID_ZIP_CHUNK_BYTES, totalSize - readTotal);
                const chunk = handle.readBytes(chunkSize);
                if (chunk.length === 0) {
                    break;
                }
                readTotal += chunk.length;
                entry.push(chunk, readTotal >= totalSize);
            }
        }
    } finally {
        handle.close();
        tempFile.delete();
    }
}

function buildSuccessMessage(fileName: string, t: any): string {
    const baseMessage = t('propertyDetails.fileDownloadedWithName').replace('{{fileName}}', fileName);
    const hint =
        typeof navigator !== 'undefined' && navigator.userAgent && /iPhone|iPad|iPod/i.test(navigator.userAgent)
            ? `\n${t('propertyDetails.iosSaveHint')}`
            : '';
    return `${baseMessage}${hint}`;
}

async function downloadSingleBrochure(url: string, safeName: string, t: any): Promise<DownloadResult> {
    logger.info('brochureDownload', 'Single-file path: fetching brochure', { url });
    const { bytes, ext } = await fetchFileBytes(url);
    const fileName = `${safeName}-brochure.${ext}`;
    const base64Content = Buffer.from(bytes).toString('base64');
    logger.info('brochureDownload', 'Single-file path: fetched, saving', {
        fileName,
        byteLength: bytes.length,
    });

    const saveResult = await downloadAndSaveFile({
        base64Content,
        fileName,
        mimeType: mapExtToMime(ext),
    });
    if (!saveResult.success || !saveResult.fileUri) {
        logger.error('brochureDownload', 'Single-file path: save failed', {
            fileName,
            error: saveResult.error,
        });
        return { success: false, error: saveResult.error || 'Failed to save file' };
    }

    logger.info('brochureDownload', 'Single-file path: saved', {
        fileName,
        fileUri: saveResult.fileUri,
    });
    return {
        success: true,
        fileUri: saveResult.fileUri,
        fileName,
        message: buildSuccessMessage(fileName, t),
    };
}

/**
 * Streams each file into the zip and writes compressed chunks straight to a
 * cache file as they're produced — only one source file's bytes are ever
 * held in memory at a time (instead of buffering all of them plus a full
 * in-memory zip output), which is what caused Android OOM crashes with
 * several sizeable brochure files.
 */
async function streamFilesToZipCacheFile(
    urls: ReadonlyArray<string>,
    safeName: string,
    zipFileName: string,
): Promise<File> {
    const cacheFile = new File(Paths.cache, zipFileName);
    cacheFile.create({ overwrite: true });
    const writer = cacheFile.writableStream().getWriter();

    let writeChain: Promise<void> = Promise.resolve();
    let zipError: Error | null = null;
    let zippedByteLength = 0;

    const zip = new Zip((err, chunk, _final) => {
        if (err) {
            zipError = err instanceof Error ? err : new Error(String(err));
            return;
        }
        zippedByteLength += chunk.length;
        writeChain = writeChain.then(() => writer.write(chunk));
    });

    for (const [i, url] of urls.entries()) {
        if (Platform.OS === 'android') {
            await streamAndroidFileIntoZipEntry(url, safeName, i, urls.length, zip);
            continue;
        }

        const { bytes, ext } = await fetchFileBytes(url);
        const entryName = `${safeName}-brochure-${i + 1}.${ext}`;
        logger.info('brochureDownload', 'Multi-file path: fetched one file, streaming into zip', {
            index: i + 1,
            of: urls.length,
            entryName,
            byteLength: bytes.length,
        });
        const entry = new ZipPassThrough(entryName);
        zip.add(entry);
        entry.push(bytes, true);
    }
    zip.end();

    await writeChain;
    if (zipError) {
        await writer.close().catch(() => undefined);
        throw zipError;
    }
    await writer.close();

    logger.info('brochureDownload', 'Multi-file path: zip streamed to cache file', {
        zipFileName,
        byteLength: zippedByteLength,
    });
    return cacheFile;
}

async function downloadAndZipBrochures(urls: ReadonlyArray<string>, safeName: string, t: any): Promise<DownloadResult> {
    logger.info('brochureDownload', 'Multi-file path: streaming files into zip', {
        fileCount: urls.length,
        urls,
    });

    const zipFileName = `${safeName}-brochure.zip`;
    const zipCacheFile = await streamFilesToZipCacheFile(urls, safeName, zipFileName);

    if (Platform.OS === 'android') {
        // Android's only way to reach the shared Downloads folder (SAF)
        // requires one full base64 string in memory, which OOMs for larger
        // zips. The file is already saved to app-private storage — hand it
        // back as-is and let the caller offer the OS share sheet instead,
        // same as iOS already does for every brochure download.
        logger.info('brochureDownload', 'Multi-file path: Android — using share flow instead of SAF write', {
            zipFileName,
            fileUri: zipCacheFile.uri,
        });
        return {
            success: true,
            fileUri: zipCacheFile.uri,
            fileName: zipFileName,
            message: buildSuccessMessage(zipFileName, t),
            requiresShareToSave: true,
        };
    }

    logger.info('brochureDownload', 'Multi-file path: reading zip cache file, saving archive', { zipFileName });
    const zipBase64 = await zipCacheFile.base64();

    const saveResult = await downloadAndSaveFile({
        base64Content: zipBase64,
        fileName: zipFileName,
        mimeType: mapExtToMime('zip'),
    });
    if (!saveResult.success || !saveResult.fileUri) {
        logger.error('brochureDownload', 'Multi-file path: save failed', {
            zipFileName,
            error: saveResult.error,
        });
        return { success: false, error: saveResult.error || 'Failed to save file' };
    }

    logger.info('brochureDownload', 'Multi-file path: saved', {
        zipFileName,
        fileUri: saveResult.fileUri,
    });
    return {
        success: true,
        fileUri: saveResult.fileUri,
        fileName: zipFileName,
        message: buildSuccessMessage(zipFileName, t),
    };
}

/**
 * Downloads one or more brochure files and saves them to the device (mobile).
 * - Single file: saved as "{projectName}-brochure.{ext}" (original format kept).
 * - Multiple files: zipped together and saved as "{projectName}-brochure.zip".
 */
export async function downloadAndPersistBrochureFiles(
    urls: ReadonlyArray<string>,
    projectName: string,
    t: any,
): Promise<DownloadResult> {
    const validUrls = urls.filter((url): url is string => Boolean(url?.trim()));
    logger.info('brochureDownload', 'downloadAndPersistBrochureFiles called', {
        projectName,
        receivedUrlCount: urls.length,
        validUrlCount: validUrls.length,
        urls: validUrls,
    });
    if (validUrls.length === 0) {
        logger.warn('brochureDownload', 'No valid brochure URLs to download', { projectName });
        return { success: false, error: 'Document not available' };
    }

    const safeName = sanitizeProjectName(projectName);
    const [singleUrl] = validUrls;
    logger.info('brochureDownload', 'Resolved download mode', {
        mode: validUrls.length === 1 ? 'single-file' : 'zip',
        safeName,
    });

    try {
        return validUrls.length === 1 && singleUrl !== undefined
            ? await downloadSingleBrochure(singleUrl, safeName, t)
            : await downloadAndZipBrochures(validUrls, safeName, t);
    } catch (error: any) {
        logger.error('brochureDownload', 'Failed to download/save brochure files', {
            error: error?.message || error,
            urlCount: validUrls.length,
        });
        return { success: false, error: error?.message || 'Failed to save file' };
    }
}

// ---------------------------------------------------------------------------
// Web: browser-triggered downloads (guest catalogue) via JSZip.
// ---------------------------------------------------------------------------

export interface DownloadBrochureFilesParams {
    readonly urls: ReadonlyArray<string>;
    /** Project / property name used in `{name}-brochure.{ext|zip}`. */
    readonly projectName: string;
}

type BrochureUrlSource = Readonly<{
    brochureUrls?: ReadonlyArray<string> | null | undefined;
    brochureUrl?: string | null | undefined;
}>;

type DocumentUrlSource = Readonly<{
    urls?: ReadonlyArray<string> | null | undefined;
    url?: string | null | undefined;
}>;

/** Prefer multi-URL list; fall back to a single URL. Avoids nested ternaries at call sites. */
export function resolveBrochureUrlsFromAssets(
    assets: BrochureUrlSource,
): string[] {
    if (assets.brochureUrls && assets.brochureUrls.length > 0) {
        return assets.brochureUrls.map((url) => url.trim()).filter(Boolean);
    }
    const single = assets.brochureUrl?.trim();
    if (single) {
        return [single];
    }
    return [];
}

/** Prefer `urls`; fall back to `url` for document download press handlers. */
export function resolveDocumentDownloadUrls(
    doc: DocumentUrlSource | null | undefined,
): string[] {
    if (!doc) {
        return [];
    }
    if (doc.urls && doc.urls.length > 0) {
        return [...doc.urls];
    }
    const single = doc.url?.trim();
    if (single) {
        return [single];
    }
    return [];
}

function sanitizeProjectNameForFile(projectName: string): string {
    const trimmed = projectName.trim() || 'project';
    return trimmed
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeFetchUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) {
        return '';
    }
    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }
    if (trimmed.startsWith('http')) {
        return trimmed;
    }
    return `https:${trimmed}`;
}

function extensionFromUrl(url: string): string {
    const pathWithoutQuery = url.split('?')[0] ?? url;
    const extMatch = /\.([a-zA-Z0-9]+)$/.exec(pathWithoutQuery);
    return (extMatch?.[1] || 'pdf').toLowerCase();
}

function fileNameFromUrl(url: string, index: number): string {
    const pathWithoutQuery = url.split('?')[0] ?? url;
    const lastSegment = pathWithoutQuery.split('/').pop() || '';
    const decoded = decodeURIComponent(lastSegment).trim();
    if (decoded && /\.[a-zA-Z0-9]+$/.test(decoded)) {
        return decoded;
    }
    return `brochure-${index + 1}.${extensionFromUrl(url)}`;
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = fileName;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    // Revoking in the same turn cancels the download before the browser reads the blob.
    window.setTimeout(() => {
        anchor.remove();
        window.URL.revokeObjectURL(blobUrl);
    }, 1500);
}

/**
 * Download Contentful brochure asset(s) on web:
 * - 1 file → `{projectName}-brochure.{format}`
 * - 2+ files → `{projectName}-brochure.zip`
 */
export async function downloadBrochureFiles(
    params: DownloadBrochureFilesParams,
): Promise<void> {
    const urls = params.urls
        .map(normalizeFetchUrl)
        .filter((url): url is string => Boolean(url));

    if (urls.length === 0) {
        return;
    }

    const baseName = sanitizeProjectNameForFile(params.projectName);

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        if (urls.length === 1) {
            const onlyUrl = urls[0]!;
            const response = await fetch(onlyUrl);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const blob = await response.blob();
            const ext = extensionFromUrl(onlyUrl);
            triggerBrowserDownload(blob, `${baseName}-brochure.${ext}`);
            return;
        }

        const zip = new JSZip();
        const usedNames = new Set<string>();

        await Promise.all(
            urls.map(async (url, index) => {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to fetch brochure asset ${index + 1}`);
                }
                const buffer = await response.arrayBuffer();
                let entryName = fileNameFromUrl(url, index);
                if (usedNames.has(entryName)) {
                    const ext = extensionFromUrl(url);
                    entryName = `brochure-${index + 1}.${ext}`;
                }
                usedNames.add(entryName);
                zip.file(entryName, buffer);
            }),
        );

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        triggerBrowserDownload(zipBlob, `${baseName}-brochure.zip`);
        return;
    }

    // Native: open first URL (multi-file zip is web-oriented for guest/catalogue).
    const firstUrl = urls[0]!;
    await Linking.openURL(firstUrl);
    if (urls.length > 1) {
        logger.warn('brochureDownload', 'Multiple brochures on native — opened first file only', {
            count: urls.length,
            projectName: params.projectName,
        });
    }
}
